import type { MentionableUser, NoteMention } from "@/services/notesService";

/**
 * The "@..." the caret currently sits in, if any.
 *
 * Names contain spaces ("Maya Waterland"), so the token is allowed to span one
 * space — enough to match a full name, while still closing the menu once the
 * author has moved on to the next word.
 */
export const ACTIVE_MENTION = /@([\p{L}\p{N}._'-]*(?:[ ][\p{L}\p{N}._'-]*)?)$/u;

const AVATAR_PALETTE = [
  "#2B4752",
  "#0C98D0",
  "#7C6BB0",
  "#2F855A",
  "#B7791F",
  "#B83280",
];

export function initials(name: string): string {
  return name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) % 100000;
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

/** Escape a display name for use inside a RegExp. */
function escapeForRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Longest name first.
 *
 * Regex alternation takes the first branch that matches, so "@Ada Lovelace"
 * must be offered before "@Ada" — otherwise tagging Ada Lovelace also tags a
 * colleague named Ada, and sends her a notification she has nothing to do with.
 */
function mentionNamesLongestFirst(names: string[]): string[] {
  return names.filter(Boolean).sort((a, b) => b.length - a.length);
}

/**
 * Matches an "@Name" for one of `names`, in a single capturing group.
 *
 * Exactly one group, and the "@" inside it, is load-bearing on both sides:
 * `String.split` keeps a separator only if it is captured, and emits *every*
 * group it finds — so a second, nested group would repeat each mention in the
 * output. The alternation is therefore non-capturing.
 */
function mentionPattern(names: string[]): RegExp {
  return new RegExp(`(@(?:${names.map(escapeForRegExp).join("|")}))`, "g");
}

/**
 * The users actually tagged in `text`.
 *
 * Derived from the text at submit time rather than tracked as the author types,
 * so deleting an "@Name" back out of the draft also drops the mention, and the
 * list can never drift from what the note actually says.
 *
 * A display name shared by two people on the trial resolves to **nobody**. The
 * text alone cannot say which one was meant, and guessing sends a notification
 * to someone the author did not tag. The picker disambiguates them by email so
 * this stays rare. The server re-resolves everything against IAM regardless.
 */
export function collectMentions(
  text: string,
  users: MentionableUser[],
): NoteMention[] {
  const byName = new Map<string, MentionableUser | null>();
  for (const user of users) {
    if (!user.name) continue;
    // Second sighting of a name marks it ambiguous rather than overwriting.
    byName.set(user.name, byName.has(user.name) ? null : user);
  }
  const names = mentionNamesLongestFirst([...byName.keys()]);
  if (!names.length) return [];

  const found: NoteMention[] = [];
  const seen = new Set<string>();
  for (const match of text.matchAll(mentionPattern(names))) {
    const user = byName.get(match[1].slice(1));
    if (!user || seen.has(user.id)) continue;
    seen.add(user.id);
    found.push({ id: user.id, name: user.name, email: user.email });
  }
  return found;
}

/** Render note text with the confirmed mentions highlighted. */
export function renderNoteText(text: string, mentions: NoteMention[]) {
  const names = mentionNamesLongestFirst(mentions.map((m) => m.name));

  // No fallback on purpose. Highlighting any "@word" made an untagged string
  // look exactly like a delivered mention, which is the one thing a mention
  // must never do — the reader cannot tell whether anyone was actually told.
  // Text that resolved to nobody stays plain text, and that is the signal.
  if (!names.length) return text;

  let offset = 0;
  return text.split(mentionPattern(names)).map((part) => {
    const key = `${offset}:${part}`;
    offset += part.length;
    return part.startsWith("@") ? (
      <span key={key} className="font-semibold text-[#0C98D0]">
        {part}
      </span>
    ) : (
      part
    );
  });
}
